import { produce } from 'immer';
import { NetworkError } from 'lib/errors';
import { isError } from 'lib/isError';
import * as actions from 'store/actions';
import { SIGN_OUT } from 'store/auth/actions';
import { ErrorState } from 'store/error/types';
import { createReducer } from 'typesafe-actions';

const initialState: ErrorState = {
  errors: [],
  hasNetworkError: false,
  isNotAuthenticated: false,
};

export const errorReducer = createReducer(initialState)
  .handleAction(actions.CATCH_ERROR, (state, action) =>
    produce(state, (draft) => {
      // Ignore network errors
      if (
        action.payload.error instanceof NetworkError &&
        !action.payload.error.isUnknown
      ) {
        return;
      }
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.error(action.payload.error);
      }
      const errorPayload = isError(action.payload.error)
        ? action.payload.error
        : typeof action.payload.error === 'string'
          ? new Error(typeof action.payload.error)
          : new Error('Invalid Error');
      // Save caught errors ready to send up to API
      const lastError = draft.errors[draft.errors.length - 1];
      const info = JSON.stringify(action.payload.info || {});
      const stack = errorPayload.stack || '';
      if (
        !lastError ||
        lastError.message !== errorPayload.message ||
        lastError.name !== errorPayload.name ||
        lastError.stack !== stack ||
        lastError.info !== info
      ) {
        draft.errors.push({
          info,
          message: errorPayload.message,
          name: errorPayload.name,
          stack,
          timestamp: new Date().toISOString(),
        });
        // Only allow 50 errors to be stored
        if (draft.errors.length > 50) {
          draft.errors.splice(50);
        }
      }
    }),
  )
  .handleAction(actions.doAuthShowAuthModal, (state, action) => ({
    ...state,
    onClear: action.payload.actionAfterAuth,
  }))
  .handleAction(actions.doAuthHideAuthModal, (state) => ({
    ...state,
    onClear: undefined,
  }))
  .handleAction(actions.SET_IS_UNAUTHORIZED, (state) => ({
    ...state,
    isNotAuthenticated: true,
  }))
  .handleAction(actions.SET_LAST_ONLINE_TIME, (state) => ({
    ...state,
    isNotAuthenticated: false,
  }))
  .handleAction(actions.UPDATE_ASSET_ERROR, (state, action) => ({
    ...state,
    asset_errors: action.payload,
  }))
  .handleAction(actions.DELETE_ASSETS, (state, action) =>
    produce(state, (draft) => {
      const assetIds = action.payload.ids;
      if (state.asset_errors && assetIds.length) {
        draft.asset_errors = Object.fromEntries(
          Object.entries(state.asset_errors).filter(
            ([assetId]) => !assetIds.includes(assetId),
          ),
        );
        if (!Object.keys(draft.asset_errors).length) {
          delete draft.asset_errors;
        }
      }
      return draft;
    }),
  )
  .handleAction(actions.CLEAR_LOCAL_ASSET_CHANGES, (state, action) =>
    produce(state, (draft) => {
      const { assetId, assetKeyId, assessmentId } = action.payload;
      if (assessmentId) {
        // Delete assessment
        delete draft.asset_errors?.[assetId]?.assessments?.[assessmentId];
        // Delete assessment object if none left
        if (
          Object.keys(draft.asset_errors?.[assetId]?.assessments || {})
            .length === 0
        ) {
          delete draft.asset_errors?.[assetId]?.assessments;
        }
      } else if (assetKeyId) {
        // Delete key from asset
        delete draft.asset_errors?.[assetId]?.[assetKeyId];
      } else {
        // Delete entire asset
        delete draft.asset_errors?.[assetId];
      }
      // Delete asset from errors, if there are no remaining errors
      if (Object.keys(draft.asset_errors?.[assetId] || {}).length === 0) {
        delete draft.asset_errors?.[assetId];
      }
      // Delete asset errors if there are no remaining assets with errors
      if (Object.keys(draft.asset_errors || {}).length === 0) {
        delete draft.asset_errors;
      }
      return draft;
    }),
  )
  .handleAction(actions.UPLOAD_ERRORS.success, (state) => ({
    ...state,
    errors: [],
  }))
  .handleAction(SIGN_OUT.success, () => initialState);
